import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Info, Globe2 } from 'lucide-react';
import { fiscalidadAPI } from '../api';

const eur = (v) => (v === null || v === undefined)
  ? '—'
  : Number(v).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

const TRIMESTRES = [1, 2, 3, 4];

export const Modelo349 = ({ ejercicio, facturadorId }) => {
  const [trimestre, setTrimestre] = useState(Math.min(4, Math.ceil((new Date().getMonth() + 1) / 3)));
  const [datos, setDatos] = useState(null);
  const [anual, setAnual] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [r, a] = await Promise.all([
        fiscalidadAPI.getModelo349(ejercicio, trimestre, facturadorId),
        fiscalidadAPI.getModelo349Anual(ejercicio, facturadorId),
      ]);
      setDatos(r.data);
      setAnual(a.data);
    } finally {
      setCargando(false);
    }
  }, [ejercicio, trimestre, facturadorId]);

  useEffect(() => { cargar(); }, [cargar]);

  if (cargando && !datos) {
    return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="modelo-349">
      <div className="flex flex-wrap items-center gap-2">
        {TRIMESTRES.map((t) => (
          <Button key={t} size="sm" variant={t === trimestre ? 'default' : 'outline'}
                  onClick={() => setTrimestre(t)} data-testid={`m349-trimestre-${t}`}>
            {t}T
          </Button>
        ))}
        {anual?.resumen && (
          <span className="text-xs text-slate-500 ml-2">
            Año {ejercicio}: {eur(anual.resumen.total_base)} con {anual.resumen.num_operadores} operador(es)
          </span>
        )}
      </div>

      {datos && !datos.obligatorio && (
        <Card data-testid="m349-sin-operaciones">
          <CardContent className="py-8 text-center space-y-2">
            <Globe2 className="w-8 h-8 mx-auto text-slate-300" />
            <p className="text-sm text-slate-600">
              No hay operaciones intracomunitarias en el {trimestre}T {ejercicio}: no hay que presentar el modelo 349.
            </p>
            <p className="text-xs text-slate-400">
              Aparecerán aquí los gastos con inversión del sujeto pasivo de proveedores de la UE
              (clave I) y las facturas emitidas sin IVA a clientes con NIF-IVA comunitario (clave S).
            </p>
          </CardContent>
        </Card>
      )}

      {datos?.obligatorio && (
        <>
          <Card data-testid="m349-operadores">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                Operadores intracomunitarios · {trimestre}T {ejercicio}
                {datos.presentada && <Badge className="ml-2" variant="secondary">presentado</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="m349-tabla">
                  <thead className="bg-slate-50 border-b">
                    <tr className="text-slate-500">
                      <th className="text-left px-3 py-2">NIF-IVA</th>
                      <th className="text-left px-3 py-2">Operador</th>
                      <th className="text-left px-3 py-2">Clave</th>
                      <th className="text-left px-3 py-2">Concepto</th>
                      <th className="text-right px-3 py-2">Base imponible</th>
                      <th className="text-right px-3 py-2">Operaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.operadores.map((o, i) => (
                      <tr key={`${o.nif}-${o.clave}-${i}`} className="border-b last:border-0"
                          data-testid={`m349-fila-${o.clave}-${i}`}>
                        <td className="px-3 py-2 font-mono">{o.nif || '(sin NIF-IVA)'}</td>
                        <td className="px-3 py-2">{o.nombre}</td>
                        <td className="px-3 py-2 font-semibold">{o.clave}</td>
                        <td className="px-3 py-2 text-slate-500">{o.concepto}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{eur(o.base)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">{o.operaciones}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-100 font-semibold">
                      <td colSpan={4} className="px-3 py-2">
                        TOTAL · {datos.num_operadores} operador(es)
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{eur(datos.total_base)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>

          {datos.por_clave?.length > 0 && (
            <Card data-testid="m349-por-clave">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Resumen por clave</CardTitle></CardHeader>
              <CardContent className="space-y-1">
                {datos.por_clave.map((c) => (
                  <div key={c.clave} className="flex justify-between text-xs py-1 border-b last:border-0">
                    <span><b className="font-mono mr-2">{c.clave}</b>{c.concepto} · {c.operadores} operador(es)</span>
                    <span className="tabular-nums font-medium">{eur(c.base)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {(datos?.avisos || []).length > 0 && (
        <div className="rounded bg-amber-50 border border-amber-200 p-3 space-y-1" data-testid="m349-avisos">
          {datos.avisos.map((a, i) => (
            <p key={i} className="text-xs text-amber-900 flex gap-2">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />{a}
            </p>
          ))}
        </div>
      )}

      <p className="text-[11px] text-slate-500">
        El 349 se presenta por trimestres (1T-3T hasta el día 20 del mes siguiente; el 4T del 1 al
        30 de enero) y pasa a ser mensual si las entregas y prestaciones superan los 50.000 € en un
        trimestre. No se ingresa nada: es informativo.
      </p>
    </div>
  );
};

export default Modelo349;
